import { KeenIcon } from '@/components';
import authService from '@/services/auth/authService';
import profileService from '@/services/setting/profileService';
import { toast } from 'sonner';
import { isSuperAdmin } from '@/utils';
import { useEffect, useRef, useState } from 'react';
import { useAuthContext } from '@/auth';
import { needsTwoFactorSetup } from '@/auth/accessControl';
import OtpInput from 'react-otp-input';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { OTPModal } from '@/components/modal'
import { setupCodeRejection } from '@/auth/twoFactorSetup';
import { FormattedMessage, useIntl } from 'react-intl';

const Authentification = () => {
  const [qrCode, setQrCode] = useState(null);
  const [seceret, setSecret] = useState(null);
  const [qrCodeError, setQrCodeError] = useState(null);
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [DisableModalOpen, setDisableModalOpen] = useState(false);
  const { auth, updateUser } = useAuthContext();
  const mustSetUp2fa = needsTwoFactorSetup(auth?.user);
  // Set when 2FA is switched on or off here, so a status reply that was
  // already on its way cannot put back the old value.
  const changedHere = useRef(false);

  // The 2FA rule on the stored user is the sign-in copy, so refresh it each
  // time this page opens (OL-157). An admin may have switched the rule off
  // (stop confining the user) or on (the server sent them here, and without
  // the fresh rule the page would not say why).
  useEffect(() => {
    if (isSuperAdmin()) return;
    authService.securityStatus()
      .then(({ data }) => {
        if (data?.security) {
          updateUser(changedHere.current
            ? { security: data.security }
            : { security: data.security, is_2fa_enabled: data.is_2fa_enabled }, true);
        }
      })
      .catch(() => {});
  }, []);
  const [resetPassModal, setResetPassModal] = useState(false)
  const [resetPassLoadnig, setResetPassLoadnig] = useState(false)
  const initialValues = {
    old_password: '',
    new_password: '',
    confirm_password: '',
    otp: ''
  };
  const { formatMessage } = useIntl();

  const passwordSchema = Yup.object().shape({
    old_password: Yup.string()
      .required(formatMessage({ id: 'user_reset_password.old_password_required' })),

    new_password: Yup.string()
      .min(8, formatMessage({ id: 'user_reset_password.password_requirements' }))
      .matches(/[A-Z]/, formatMessage({ id: 'user_reset_password.password_uppercase' }))
      .matches(/[a-z]/, formatMessage({ id: 'user_reset_password.password_lowercase' }))
      .matches(/[0-9]/, formatMessage({ id: 'user_reset_password.password_number' }))
      .matches(/[^A-Za-z0-9]/, formatMessage({ id: 'user_reset_password.password_special_char' }))
      .required(formatMessage({ id: 'user_reset_password.password_required' }))
      .notOneOf([Yup.ref('old_password')], formatMessage({ id: 'user_reset_password.new_password_different' })),

    confirm_password: Yup.string()
      .required(formatMessage({ id: 'user_reset_password.confirm_password' }))
      .oneOf([Yup.ref('new_password')], formatMessage({ id: 'user_reset_password.passwords_must_match' })),
  });

  const enable2fa = async () => {
    setModalOpen(true);
    setIsLoading(true);
    setQrCode(null);
    setSecret(null);
    setQrCodeError(null);
    try {
      const response = isSuperAdmin() 
        ? await authService.superAdminEnable2fa() 
        : await authService.enable2fa();

      if (response && response.data.qr_code) {
        setQrCode(response.data.qr_code)
        setSecret(response.data.secret)
      } else {
        toast.error(formatMessage({ id: 'account_security.failed_to_fetch_qr_code' }), {
          description: formatMessage({ id: 'account_security.error_enabling_2fa' }),

        });
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error?.error || error?.message || formatMessage({ id: 'account_security.something_went_wrong' });

      if (errorMessage === 'Too Many Attempts.') {
        setQrCodeError(errorMessage);

        toast.warning(formatMessage({ id: 'account_security.verification_unavailable' }), {
          description: formatMessage({ id: 'account_security.please_wait_and_try_again' }),

        });
      } else {
        setQrCodeError(formatMessage({ id: 'account_security.unexpected_error' }));

        toast.error(formatMessage({ id: 'account_security.connection_error' }), {
          description: formatMessage({ id: 'account_security.error_enabling_2fa_connection_error' }),

        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const disable2fa = () => {
    setDisableModalOpen(true);
  };

  const handleSubmitOTP = async () => {
    setIsLoading(true);
    try {
      const response = isSuperAdmin() 
        ? await authService.superAdminVerify2faOTP({ otp: otp }) 
        : await authService.verify2faOTP({ otp: otp });
      if (response.status) {
        const user = response.data.user;
        changedHere.current = true;
        const updatedUser = {
          is_2fa_enabled: 1
        }
        updateUser(updatedUser, true)
        setModalOpen(false)
        setOtp('')
        toast.success(formatMessage({ id: 'account_security.2fa_enabled_successfully' }), {
          description: formatMessage({ id: 'account_security.two_factor_authentication_enabled' }),
        })
      }
    } catch (error) {
      console.error('Error:', error.message);
      const rejection = setupCodeRejection(error.message);
      if (rejection) {
        toast.warning(formatMessage({ id: 'account_security.invalid_otp' }), {
          description: rejection,
          duration: 15000,
        });
        return
      }
      toast.error(formatMessage({ id: 'account_security.error_verifying_otp' }), {
        description: formatMessage({ id: 'account_security.failed_to_verify_otp' }),

      });
    } finally {
      setOtp('')
      setIsLoading(false);
    }
  };

  const handleSubmitDisable2fa = async () => {
    setIsLoading(true);
    try {
      const response = isSuperAdmin() 
        ? await authService.superAdminDisableVerify2faOTP({ otp: otp }) 
        : await authService.disableVerify2faOTP({ otp: otp });
      if (response.status) {
        setDisableModalOpen(false)
        const user = response.data.user;
        changedHere.current = true;
        const updatedUser = {
          is_2fa_enabled: 0
        }
        updateUser(updatedUser, true)
        setOtp('')
        toast.success('2FA Disabled successfully', {
          description: 'Two-Factor Authentication has been successfully disabled.',
        })
      }
    } catch (error) {
      console.error('Error:', error.message);
      if (error.message === 'OTP invalid or expired') {
        toast.error('Invalid OTP', {
          description: 'Failed to verify because OTP was invalid or expired. Please try again.',

        });
        return
      }
      // The server's reason when it gives one, e.g. the firm requires 2FA.
      toast.error('Could not turn off 2FA', {
        description: error?.message || error?.customMessage || 'Failed to verify OTP. Please try again.',

      });
    } finally {
      setIsLoading(false);
      setOtp('')
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false)
    setOtp('')
  }

  const handleCloseResetModal = () => {
    setResetPassModal(false)
    // setOtp('')
  }

  const handleCloseDisableModal = () => {
    setDisableModalOpen(false)
    setOtp('')
  }

  const handlePasswordChange = async (values, { setErrors }) => {
    const payload = {
      ...values,
      otp: otp
    }
    // return console.log({values, payload})
    setResetPassLoadnig(true)
    try {
      const { status } = isSuperAdmin() 
        ? await profileService.superAdminResetPassword(payload) 
        : await profileService.resetPassword(payload);
      if (status) {
        setResetPassModal(false)
        toast.success(formatMessage({ id: 'user_reset_password.password_success' }), {
          description: formatMessage({ id: 'user_reset_password.password_changed_successfully' }),

        });
      }
    } catch (error) {
      console.error('Error:', error.message);
      if (error.message === 'Old password is incorrect') {
        setErrors({ old_password: 'Old password is incorrect' })
        toast.warning(formatMessage({ id: 'user_reset_password.old_password_incorrect' }), {
          description: formatMessage({ id: 'user_reset_password.correct_old_password' }),

        });
        return
      } else if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const responseErrors = error.errors || {};
        setErrors(responseErrors)
        toast.warning(formatMessage({ id: 'validation.validation_error' }), {
          description: formatMessage({ id: 'validation.validation_message' }),
        });
        return
      } else {
        toast.error(formatMessage({ id: 'user_reset_password.something_went_wrong' }), {
          description: formatMessage({ id: 'user_reset_password.something_went_wrong_try_again' }),
        });
      }
    } finally {
      setOtp('')
      setResetPassLoadnig(false)
    }
  }

  return <div className="card min-w-full">
    <div className="card-header">
      <h3 className="card-title"><FormattedMessage id="account_security.authentication" /></h3>
    </div>
    {mustSetUp2fa && (
      <div className="mx-5 mt-4 rounded-md border border-warning bg-warning-light px-4 py-3 text-sm text-gray-800">
        Your firm requires two-factor authentication. Turn on 2FA below to continue using OAK.LEGAL.
      </div>
    )}
    <div className="card-table scrollable-x-auto pb-3">
      <table className="table align-middle text-sm text-gray-500">
        <tbody>
          <tr>
            <td className="text-gray-600 font-normal"><FormattedMessage id="account_security.password" /></td>
            <td className="text-gray-700 font-normal"><FormattedMessage id="account_security.reset_password" /></td>
            <td className="text-end">
              <button onClick={() => { setResetPassModal(true) }} className="btn btn-sm btn-icon btn-icon-lg link">
                <KeenIcon icon="notepad-edit" />
              </button>
            </td>
          </tr>
          <tr>
            <td className="text-gray-600 font-normal">2FA</td>
            <td className="text-gray-700 font-normal">
              {auth?.user?.is_2fa_enabled == 1 &&
                <span className="badge badge-sm badge-outline badge-success"><FormattedMessage id="user_profile.enabled" /></span>}
              {(auth?.user?.is_2fa_enabled == 0 || auth?.user?.is_2fa_enabled == null) &&
                <span className="badge badge-sm badge-outline"><FormattedMessage id="user_profile.to_be_set" /></span>}
            </td>
            <td className="text-end">
              {auth?.user?.is_2fa_enabled == 1 &&
                <button onClick={disable2fa} className="btn btn-link btn-sm">
                  <FormattedMessage id="user_reset_password.disable" />
                </button>
              }
              {(auth?.user?.is_2fa_enabled == 0 || auth?.user?.is_2fa_enabled == null) &&
                <button onClick={enable2fa} className="btn btn-link btn-sm">
                  <FormattedMessage id="user_reset_password.setup" />
                </button>
              }
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    {/* Enable 2FA */}
    <OTPModal
      open={modalOpen}
      onClose={handleCloseModal}
      onSubmit={handleSubmitOTP}
      otp={otp}
      setOtp={setOtp}
      isLoading={isLoading}
      title={formatMessage({ id: 'user_reset_password.choose_authentication_method' })}
      description={
        <>
          <h3 className="text-lg font-medium text-gray-900"><FormattedMessage id="user_reset_password.authenticator_apps" /></h3>
          <p className="text-sm">
            <FormattedMessage
              id="authenticatorPrompt"
              values={{
                googleLink: (text) => (
                  <a
                    href="https://support.google.com/accounts/answer/1066447?hl=en"
                    target="_blank"
                    className="link"
                  >
                    {text}
                  </a>
                ),
                microsoftLink: (text) => (
                  <a
                    href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
                    target="_blank"
                    className="link"
                  >
                    {text}
                  </a>
                ),
                authyLink: (text) => (
                  <a
                    href="https://www.authy.com/download/"
                    target="_blank"
                    className="link"
                  >
                    {text}
                  </a>
                ),
                onePasswordLink: (text) => (
                  <a
                    href="https://support.1password.com/one-time-passwords/?windows"
                    target="_blank"
                    className="link"
                  >
                    {text}
                  </a>
                ),
              }}
            />
          </p>
        </>
      }
      qrCode={qrCode}
      qrCodeError={qrCodeError}
      secret={seceret}
      disable2FA={false}
    />
    {/* Disable 2FA */}
    <OTPModal
      open={DisableModalOpen}
      onClose={handleCloseDisableModal}
      onSubmit={handleSubmitDisable2fa}
      otp={otp}
      setOtp={setOtp}
      isLoading={isLoading}
      title={formatMessage({ id: 'user_reset_password.disable_authentication_method' })}
      description={
        <>
          <h3 className="text-lg font-medium text-gray-900"><FormattedMessage id="user_reset_password.authenticator_apps" /></h3>
          <p className="text-sm">
            <FormattedMessage
              id="authenticatorPrompt"
              values={{
                googleLink: (text) => (
                  <a
                    href="https://support.google.com/accounts/answer/1066447?hl=en"
                    target="_blank"
                    className="link"
                  >
                    {text}
                  </a>
                ),
                microsoftLink: (text) => (
                  <a
                    href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
                    target="_blank"
                    className="link"
                  >
                    {text}
                  </a>
                ),
                authyLink: (text) => (
                  <a
                    href="https://www.authy.com/download/"
                    target="_blank"
                    className="link"
                  >
                    {text}
                  </a>
                ),
                onePasswordLink: (text) => (
                  <a
                    href="https://support.1password.com/one-time-passwords/?windows"
                    target="_blank"
                    className="link"
                  >
                    {text}
                  </a>
                ),
              }}
            />
          </p>
        </>
      }
      disable2FA={true}
    />

    {resetPassModal && (
      <div className="modal-wrapper modal-animate-fade">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={passwordSchema}
          onSubmit={handlePasswordChange}
        >
          {({ errors, values, handleChange, handleSubmit, setFieldValue }) => (
            <Form className="modal-body modal-animate-scale w-4/5 max-w-xl">
              <h3 className="card-title capitalize mb-5"><FormattedMessage id="user_reset_password.reset_password" /></h3>
              <button
                type="button"
                onClick={handleCloseResetModal}
                className="modal-close-btn"
              >
                <KeenIcon icon="cross" />
              </button>

              {/* <div className='flex items-center gap-5'> */}
              <div className='w-full mb-5'>
                <label className="form-label mb-1.5"><FormattedMessage id="user_reset_password.old_password" /></label>
                <Field name="old_password" className="input" />
                <ErrorMessage name="old_password" component="div" className="text-red-500 text-xs mt-1" />
              </div>

              <div className='w-full mb-5'>
                <label className="form-label mb-1.5"><FormattedMessage id="user_reset_password.new_password" /></label>
                <Field name="new_password" className="input" />
                <ErrorMessage name="new_password" component="div" className="text-red-500 text-xs mt-1" />
              </div>

              <div className='w-full mb-5'>
                <label className="form-label mb-1.5"><FormattedMessage id="user_reset_password.confirm_new_password" /></label>
                <Field name="confirm_password" className="input" />
                <ErrorMessage name="confirm_password" component="div" className="text-red-500 text-xs mt-1" />
              </div>
              {auth.user?.is_2fa_enabled == 1 && (
                <div className='w-full mb-5'>
                  <label className="form-label text-gray-900 mb-1.5"><FormattedMessage id="user_reset_password.enter_otp" /></label>
                  <label className='flex flex-wrap justify-start gap-2.5'>
                    <OtpInput
                      value={otp}
                      onChange={setOtp}
                      numInputs={6}
                      isInputNum
                      shouldAutoFocus
                      containerStyle="flex justify-center gap-2"
                      renderInput={(props) => (
                        <input
                          {...props}
                          className="input focus:border-primary-clarity focus:ring focus:ring-primary-clarity !size-10 shrink-0 px-0 text-center"
                        />
                      )}
                    />
                  </label>
                </div>
              )}

              <button type="submit" disabled={resetPassLoadnig} className="btn btn-primary justify-center w-full mt-2">
                {resetPassLoadnig ? <FormattedMessage id="USER.MENU.PLEASE_WAIT" /> : <FormattedMessage id="user_reset_password.reset_password" />}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    )}
  </div>;
};
export { Authentification };